import React, { useEffect } from 'react';

import { useRouter } from 'next/router';

import { useTranslation } from 'i18n/hooks';
import { useCountries } from 'i18n/hooks/useCountries';
import { Spinner } from 'revibe-ui';
import { SpacedContainer } from 'revibe-ui';

import { Page404 } from 'modules/auth/pages/Page404';
import Image from 'next/image';
import { Layout } from 'modules/common/components/Layout';
import { RatingTrustpilot } from 'modules/common/components/RatingTrustpilot';
import { ShopPage } from 'modules/common/components/ShopPage';
import { ItemCollectionBanner } from 'modules/item/components/ItemCollectionBanner';
import { ItemDetailedView } from 'modules/item/components/ItemDetailedView';
import { ItemsCarousel } from 'modules/item/components/ItemsCarousel';
import { ITEM_FILTERS } from 'modules/item/utils/itemFilter';
import { ITEM_STATUSES } from 'modules/item/utils/itemStatus';
import { useDataLayer } from 'modules/tracking/hooks/useDataLayer';

import { ClampText } from 'ui/components/ClampText';
import { MarketplaceAPI } from 'revibe-api';
import {
  CheckBadgeIcon,
  CreditCardIcon,
  TruckIcon,
} from '@heroicons/react/24/outline';
import Link from 'next/link';
import { COMPANY_ROUTES } from 'routes';

type Props = { item?: MarketplaceAPI.Item | null };

export const ItemDetailPage = ({ item = null }: Props) => {
  const router = useRouter();
  const { slug, itemID } = router.query;
  const { tu } = useTranslation('shop');
  const { getCountryByCode } = useCountries();
  const dataLayer = useDataLayer();
  useEffect(() => {
    if (!item) {
      return;
    }
    MarketplaceAPI.trackItemView(item.id);
    dataLayer.trackViewItem(item);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item, slug, itemID]);

  if (router.isFallback) {
    return (
      <ShopPage title={item?.name || 'Item page'} isShopPage>
        <Layout minHeight centered>
          <Spinner />
        </Layout>
      </ShopPage>
    );
  }

  if (item === null) {
    return <Page404 />;
  }

  const incentives = [
    {
      name: tu('orders.delivery-free-shipping'),
      icon: TruckIcon,
      to: COMPANY_ROUTES.DELIVERY_AND_RETURNS,
    },
    {
      name: tu('14-days-return'),
      icon: CheckBadgeIcon,
      to: COMPANY_ROUTES.DELIVERY_AND_RETURNS,
    },
    {
      name: tu('secure-payments'),
      icon: CreditCardIcon,
      to: COMPANY_ROUTES.PAYMENTS,
    },
  ];

  return (
    <>
      <ShopPage title={item?.name || 'Item page'} isShopPage>
        <div className="border-black lg:border-b">
          <ItemDetailedView item={item} />
        </div>
        {item.seller.description !== null && !item.seller.is_revibe_owned && (
          <div className="border-b border-black px-4 py-8">
            <div className="relative flex grid-cols-2 flex-col gap-4 md:grid">
              <SpacedContainer className="flex-1 text-black">
                <div>
                  <h1 className="font-formula text-3xl md:text-4xl lg:text-5xl">
                    {item.seller.name}
                  </h1>
                  <p className="text-sm text-gray-400">
                    {getCountryByCode(item.seller.country)}
                  </p>
                </div>
                <ClampText lines={6}>
                  <p>{item.seller.description}</p>
                </ClampText>
              </SpacedContainer>
              {item.seller.mkt_image ? (
                <Image
                  className="object-contain"
                  fill
                  src={
                    item.seller.mkt_image ||
                    'https://res.cloudinary.com/revibe/image/upload/v1639411886/home/Frame_17_ocht0g.svg'
                  }
                  alt={item.seller.name}
                />
              ) : (
                <div />
              )}
            </div>
          </div>
        )}
        {item.collection && (
          <div className="border-b border-black px-4">
            <ItemCollectionBanner learnMore collection={item.collection} />
          </div>
        )}
        <SpacedContainer className="border-b border-black bg-gray-50 px-4 py-8">
          <div className="">
            <h2 className="sr-only">Why you should buy from us</h2>
            <div className="flex overflow-x-auto">
              <div className="space-12 mx-auto grid grid-cols-1 gap-4 whitespace-nowrap py-3 px-4 sm:px-6 md:grid-cols-3 md:gap-12 lg:gap-24 lg:px-8">
                {incentives.map((incentive) => (
                  <Link
                    href={incentive.to}
                    key={incentive.name}
                    className="mx-auto inline-flex w-fit items-center text-sm font-medium text-black"
                  >
                    <incentive.icon
                      className="mr-2 h-6 w-6 flex-none"
                      aria-hidden="true"
                    />
                    <p>{incentive.name}</p>
                  </Link>
                ))}
              </div>
            </div>
          </div>

          <RatingTrustpilot starClassName="fill-black h-8 w-8 mt-2" />
        </SpacedContainer>

        <div className="my-12">
          <ItemsCarousel
            list={`Related products from ${item.seller.name}`}
            label={`${tu('more-from')} ${item.seller.name}`}
            filters={{
              [ITEM_FILTERS.STATUS]: ITEM_STATUSES.APPROVED,
              [ITEM_FILTERS.SELLER]: item.seller.id,
              [ITEM_FILTERS.EXCLUDE]: item.id,
              [ITEM_FILTERS.SEX]: item.sex,
            }}
          />
        </div>
        <div className="my-16">
          <ItemsCarousel
            list={`Related products from ${item.category.slug}`}
            label={`${tu('more-from-category')}, ${tu(
              item.category.slug,
              'shop.categories'
            )}`}
            filters={{
              [ITEM_FILTERS.CATEGORY]: item.category.slug,
              [ITEM_FILTERS.EXCLUDE]: item.id,
              [ITEM_FILTERS.SEX]: item.sex,
            }}
          />
        </div>
      </ShopPage>
    </>
  );
};
